/**
 * Global Application Constants
 *
 * Centralized constants for pagination, limits, and other global values.
 * Enforced across all Firestore queries to prevent runaway costs.
 */

/**
 * Pagination Constants
 *
 * These limits prevent unbounded Firestore queries that can cause:
 * - High read costs (500 reads = $0.18 per query at scale)
 * - Performance issues (large result sets)
 * - Memory exhaustion
 */
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 100;

export const PAGINATION_LIMITS = {
  /** Employee list queries (admin dashboard, org chart) */
  employees: 50,

  /** Goals/OKR queries (manager dashboard, employee goals) */
  goals: 50,

  /** Time-off request queries (admin approvals, employee history) */
  timeOffRequests: 50,

  /** Notification queries (bell icon, notification center) */
  notifications: 20,

  /** Audit log queries (admin audit trail) */
  auditLogs: 100,

  /** Document queries (knowledge base, file browser) */
  documents: 30,

  /** Shift queries (schedule view, time tracking) */
  shifts: 50,

  /** Expense report queries (admin approvals, employee submissions) */
  expenseReports: 50,
} as const;

/**
 * Query Timeouts (milliseconds)
 *
 * Maximum time to wait for Firestore queries before showing error
 */
export const QUERY_TIMEOUTS = {
  /** Standard queries (lists, searches) */
  default: 10000, // 10 seconds

  /** Complex aggregations (reports, analytics) */
  complex: 30000, // 30 seconds

  /** Background operations (exports, bulk updates) */
  background: 60000, // 60 seconds
} as const;

/**
 * Cache Durations (milliseconds)
 *
 * How long to cache data before refetching
 */
export const CACHE_DURATIONS = {
  /** Static reference data (countries, timezones) */
  static: 86400000, // 24 hours

  /** Tenant settings (rarely change) */
  settings: 300000, // 5 minutes

  /** Employee data (changes moderately) */
  employees: 60000, // 1 minute

  /** Real-time data (notifications, approvals) */
  realtime: 0, // No caching
} as const;

/**
 * File Upload Limits
 */
export const UPLOAD_LIMITS = {
  /** Maximum file size in bytes (10MB) */
  maxFileSize: 10 * 1024 * 1024,

  /** Allowed file types for documents */
  allowedDocumentTypes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/gif',
  ],

  /** Allowed file types for profile pictures */
  allowedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
} as const;

/**
 * Validation Limits
 */
export const VALIDATION_LIMITS = {
  /** Maximum length for text fields */
  shortText: 255,
  mediumText: 1000,
  longText: 5000,

  /** Employee name limits */
  employeeName: 100,

  /** Company name limit */
  companyName: 200,

  /** Email length (RFC 5321) */
  email: 254,

  /** Salary range (in cents to avoid decimals) */
  minSalary: 0,
  maxSalary: 10000000, // 10M
} as const;
